/**
 * "Excel Study" free course for /excel-course.
 *
 * Imported from the video project by scripts/import-excel-course.mjs — re-run
 * that script after new classes are made; never edit the JSON by hand.
 * Videos are vertical YouTube Shorts; thumbnails are our own 540×960 WebPs.
 */
import data from './excelCourse.data.json';

export type ExcelLevel = 'basics' | 'intermediate' | 'advanced' | 'pro';

export interface ExcelModule {
  module: number;
  name: string;
  classes: string;   // "1-10"
  level: ExcelLevel;
  made: number;
}

export interface ExcelClass {
  class: number;
  module: string;
  topic: string;
  title: string;
  description: string;
  video_id: string;
  youtube_url: string;
  level: ExcelLevel;
  ai_prompt: string;
  ai_answer: string;
  sources: string[];
  length_seconds: number;
  thumbnail: string;
  upload_date: string | null;
}

export interface UpcomingClass {
  class: number;
  module: string;
  topic: string;
  level: ExcelLevel;
  needs_excel: string;
}

export const excelCourse = {
  name: data.course,
  by: data.by,
  planned: data.planned_classes,
  made: data.made,
};

export const excelModules = data.modules as ExcelModule[];
export const excelClasses = data.classes as ExcelClass[];
export const upcomingClasses = data.upcoming as UpcomingClass[];

export const SITE = 'https://acescommerce.com';
export const classPath = (n: number) => `/excel-course/class-${n}/`;

export const getExcelClass = (n: number): ExcelClass | undefined =>
  excelClasses.find((c) => c.class === n);

export const moduleRange = (m: ExcelModule): [number, number] => {
  const [a, b] = m.classes.split('-').map(Number);
  return [a ?? 0, b ?? a ?? 0];
};

export const moduleTotal = (m: ExcelModule) => {
  const [a, b] = moduleRange(m);
  return b - a + 1;
};

/** The course counts 16 numbered modules; "7.5" (modern functions) is an extra one. */
export const moduleCount = excelModules.filter((m) => Number.isInteger(m.module)).length;

export const moduleLabel = (m: ExcelModule) => (Number.isInteger(m.module) ? `Module ${m.module}` : 'Extra module');

export const moduleAnchor = (m: ExcelModule) => `module-${String(m.module).replace('.', '-')}`;

export const getModule = (name: string): ExcelModule | undefined => excelModules.find((m) => m.name === name);

export const lengthLabel = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

export const isoDuration = (s: number) => `PT${Math.floor(s / 60)}M${s % 60}S`;

export const levelLabel: Record<ExcelLevel, string> = {
  basics: 'Basics',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  pro: 'Pro',
};

/** Readable name for a Microsoft support link, from its URL slug. */
export function sourceLabel(url: string): string {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    const slug = parts[parts.length - 1] ?? '';
    const words = slug.replace(/-[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, '').split('-');
    const text = words.join(' ');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : url;
  } catch {
    return url;
  }
}
